import React, { useState, useEffect } from 'react';
import taskService from '../services/taskService';
import { toast } from 'react-hot-toast';
import {
    CheckCircle, Clock, Plus, Layout, List as ListIcon,
    MoreHorizontal, Search, Sparkles, User
} from 'lucide-react';

// --- INTERNAL COMPONENTS (Robust, No External Deps) ---

// Phase 07.1 Wave 2: Card/Badge/Button/Input swept to shadcn theme tokens.
// Status-encoded badge variants (info/success/warning/error/purple) retain semantic
// palette colors — these encode priority/status, not theme. Neutral badge uses theme.
const Card = ({ children, className = "" }) => (
    <div className={`bg-card text-card-foreground border border-border rounded-xl shadow-sm overflow-hidden ${className}`}>
        {children}
    </div>
);

const Badge = ({ children, variant = 'neutral', className = "" }) => {
    const variants = {
        neutral: "bg-muted text-muted-foreground border-border",
        info: "bg-sky-50 text-sky-700 border-sky-200",            // keep: semantic info (sky avoids theme-blue conflict)
        success: "bg-emerald-50 text-emerald-700 border-emerald-200", // keep: semantic success
        warning: "bg-amber-50 text-amber-700 border-amber-200",   // keep: semantic warning
        error: "bg-red-50 text-red-700 border-red-200",           // keep: semantic error
        purple: "bg-purple-50 text-purple-700 border-purple-200", // keep: semantic ai-tag
    };
    return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold border uppercase tracking-wider ${variants[variant] || variants.neutral} ${className}`}>
            {children}
        </span>
    );
};

const Button = ({ children, variant = "primary", size = "default", onClick, disabled, className = "" }) => {
    const base = "inline-flex items-center justify-center rounded-lg font-medium transition-colors focus:outline-none focus:ring-2 ring-ring focus:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none";
    const variants = {
        primary: "bg-primary text-primary-foreground hover:opacity-90 shadow-sm",
        secondary: "bg-card text-foreground border border-border hover:bg-muted",
        ghost: "hover:bg-muted text-muted-foreground",
    };
    const sizes = {
        default: "h-9 px-4 py-2 text-sm",
        sm: "h-8 px-3 text-xs",
        icon: "h-9 w-9",
    };
    return (
        <button onClick={onClick} disabled={disabled} className={`${base} ${variants[variant] || variants.primary} ${sizes[size] || sizes.default} ${className}`}>
            {children}
        </button>
    );
};

const Input = ({ className = "", ...props }) => (
    <input
        className={`flex h-9 w-full rounded-lg border border-input bg-popover text-foreground px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
        {...props}
    />
);

// --- HELPER COMPONENT: TASK COLUMN ---

const TaskColumn = ({ title, status, tasks, onDrop, onEdit }) => {
    const handleDragOver = (e) => e.preventDefault();
    const handleDrop = (e) => {
        const taskId = e.dataTransfer.getData("taskId");
        if (taskId) onDrop(taskId, status);
    };

    return (
        <div
            className="flex-1 min-w-[300px] bg-muted rounded-xl border border-border flex flex-col h-full"
            onDragOver={handleDragOver}
            onDrop={handleDrop}
        >
            <div className="p-4 border-b border-border flex justify-between items-center bg-card rounded-t-xl">
                <div className="flex items-center gap-2">
                    <h3 className="font-bold text-foreground text-xs uppercase tracking-wider">{title}</h3>
                    <Badge variant="neutral">{tasks.length}</Badge>
                </div>
                <MoreHorizontal className="w-4 h-4 text-muted-foreground cursor-pointer hover:text-foreground" />
            </div>

            <div className="p-3 space-y-3 overflow-y-auto flex-1 custom-scrollbar">
                {tasks.map(task => (
                    <div
                        key={task.id}
                        draggable
                        onDragStart={(e) => e.dataTransfer.setData("taskId", task.id)}
                        className="bg-card p-4 rounded-lg border border-border shadow-sm cursor-move hover:shadow-md hover:border-primary transition-all group relative"
                        onClick={() => onEdit(task)}
                    >
                        <div className="flex justify-between items-start mb-2">
                            <Badge
                                variant={
                                    task.priority === 'URGENT' ? 'error' :
                                        task.priority === 'HIGH' ? 'warning' : 'neutral'
                                }
                            >
                                {task.priority}
                            </Badge>
                            {task.is_ai_generated && <Sparkles className="w-3 h-3 text-purple-500" />}
                        </div>
                        <h4 className="font-semibold text-foreground text-sm mb-1 group-hover:text-primary leading-tight">
                            {task.title}
                        </h4>
                        <p className="text-xs text-muted-foreground line-clamp-2 mb-3">{task.description}</p>

                        <div className="flex items-center justify-between pt-3 border-t border-border">
                            <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground font-medium">
                                <Clock className="w-3 h-3" />
                                {task.due_date ? new Date(task.due_date).toLocaleDateString() : 'No Date'}
                            </div>
                            <div className="h-6 w-6 rounded-full bg-muted border border-card shadow-sm flex items-center justify-center text-muted-foreground">
                                <User size={12} />
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

// --- MAIN PAGE ---

const TasksPage = () => {
    const [tasks, setTasks] = useState([]);
    const [viewMode, setViewMode] = useState('board');
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');

    useEffect(() => {
        fetchTasks();
    }, []);

    const fetchTasks = async () => {
        setLoading(true);
        try {
            // 🔴 `firm_wide: true` — ЯВНАЯ просьба показать задачи всей фирмы
            // (11.09.2026, 260911-2cf). Доска `/tasks` штатная, и умолчание у
            // ручки теперь «свои задачи»: без этого параметра сотрудник увидел
            // бы здесь только собственные. Нештатной роли ручка на эту просьбу
            // отвечает 403 — это «нет права», а не «нет задач».
            const data = await taskService.getTasks({ firm_wide: true });
            setTasks(data || []);
        } catch (error) {
            toast.error("Failed to load tasks");
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleDrop = async (taskId, newStatus) => {
        const originalTasks = [...tasks];
        setTasks(prev => prev.map(t => t.id === parseInt(taskId) ? { ...t, status: newStatus } : t));

        try {
            await taskService.updateTask(taskId, { status: newStatus });
            toast.success(`Moved to ${newStatus}`);
        } catch (error) {
            setTasks(originalTasks);
            toast.error("Failed to update status");
        }
    };

    const handleCreateTask = async () => {
        try {
            const newTask = await taskService.createTask({
                title: "New Task " + new Date().toLocaleTimeString(),
                priority: "MEDIUM",
                status: "TODO"
            });
            setTasks([newTask, ...tasks]);
            toast.success("Task Created");
        } catch (error) {
            toast.error("Creation failed");
        }
    };

    const filteredTasks = tasks.filter(t =>
        t.title.toLowerCase().includes(search.toLowerCase())
    );

    const columns = {
        TODO: filteredTasks.filter(t => t.status === 'TODO'),
        IN_PROGRESS: filteredTasks.filter(t => t.status === 'IN_PROGRESS'),
        REVIEW: filteredTasks.filter(t => t.status === 'REVIEW'),
        DONE: filteredTasks.filter(t => t.status === 'DONE'),
    };

    return (
        <div className="min-h-screen bg-background text-foreground p-6 flex flex-col h-screen overflow-hidden">
            {/* Header */}
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-foreground tracking-tight">Task Center</h1>
                    <p className="text-muted-foreground text-sm mt-1">Manage case work and AI action items</p>
                </div>
                <div className="flex gap-3">
                    <div className="flex bg-card rounded-lg p-1 border border-border shadow-sm">
                        <button
                            onClick={() => setViewMode('board')}
                            className={`p-2 rounded-md transition-all ${viewMode === 'board' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                        >
                            <Layout className="w-4 h-4" />
                        </button>
                        <button
                            onClick={() => setViewMode('list')}
                            className={`p-2 rounded-md transition-all ${viewMode === 'list' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                        >
                            <ListIcon className="w-4 h-4" />
                        </button>
                    </div>
                    <Button onClick={handleCreateTask} className="gap-2">
                        <Plus className="w-4 h-4" /> New Task
                    </Button>
                </div>
            </div>

            {/* AI Inbox — purple gradient retained as semantic AI-tag aesthetic */}
            <Card className="mb-6 bg-gradient-to-r from-purple-50 to-white border-purple-100">
                <div className="py-4 px-6 flex flex-row items-center gap-4">
                    <div className="p-2.5 bg-purple-100 rounded-xl shadow-sm border border-purple-200">
                        <Sparkles className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                        <h3 className="text-sm font-bold text-foreground">AI Suggestions</h3>
                        <p className="text-xs text-muted-foreground mt-0.5">The AI has identified potential action items from recent case files.</p>
                    </div>
                </div>
            </Card>

            {/* Content View */}
            {loading ? (
                <div className="flex-1 flex items-center justify-center">
                    <div className="w-8 h-8 border-4 border-border border-t-primary rounded-full animate-spin"></div>
                </div>
            ) : viewMode === 'board' ? (
                <div className="flex-1 overflow-x-auto pb-4">
                    <div className="flex gap-6 h-full min-w-[1000px]">
                        <TaskColumn title="To Do" status="TODO" tasks={columns.TODO} onDrop={handleDrop} onEdit={() => { }} />
                        <TaskColumn title="In Progress" status="IN_PROGRESS" tasks={columns.IN_PROGRESS} onDrop={handleDrop} onEdit={() => { }} />
                        <TaskColumn title="Review" status="REVIEW" tasks={columns.REVIEW} onDrop={handleDrop} onEdit={() => { }} />
                        <TaskColumn title="Done" status="DONE" tasks={columns.DONE} onDrop={handleDrop} onEdit={() => { }} />
                    </div>
                </div>
            ) : (
                <Card className="flex-1 overflow-hidden flex flex-col">
                    <div className="p-4 border-b border-border flex gap-4 bg-muted/50">
                        <div className="relative max-w-sm w-full">
                            <Search className="absolute left-3 top-2.5 w-4 h-4 text-muted-foreground" />
                            <Input
                                placeholder="Search tasks..."
                                className="pl-9"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="overflow-auto flex-1">
                        <table className="w-full text-sm text-left">
                            <thead className="text-xs text-muted-foreground uppercase bg-muted border-b border-border sticky top-0">
                                <tr>
                                    <th className="px-6 py-3 font-medium">Task</th>
                                    <th className="px-6 py-3 font-medium">Status</th>
                                    <th className="px-6 py-3 font-medium">Priority</th>
                                    <th className="px-6 py-3 font-medium">Due Date</th>
                                    <th className="px-6 py-3 font-medium text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredTasks.map((task) => (
                                    <tr key={task.id} className="bg-card border-b border-border hover:bg-muted transition-colors">
                                        <td className="px-6 py-4 font-medium text-foreground">
                                            {task.title}
                                            {task.is_ai_generated && <Sparkles className="w-3 h-3 text-purple-500 inline ml-2" />}
                                            <div className="text-xs text-muted-foreground font-normal mt-0.5">{task.description}</div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <Badge variant={
                                                task.status === 'DONE' ? 'success' :
                                                    task.status === 'IN_PROGRESS' ? 'info' : 'neutral'
                                            }>
                                                {task.status}
                                            </Badge>
                                        </td>
                                        <td className="px-6 py-4">
                                            <Badge variant={
                                                task.priority === 'URGENT' ? 'error' :
                                                    task.priority === 'HIGH' ? 'warning' : 'neutral'
                                            }>
                                                {task.priority}
                                            </Badge>
                                        </td>
                                        <td className="px-6 py-4 text-muted-foreground">
                                            {task.due_date ? new Date(task.due_date).toLocaleDateString() : '-'}
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                                <MoreHorizontal className="w-4 h-4" />
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Card>
            )}
        </div>
    );
};

export default TasksPage;
